import {
  successColor,
  infoColor,
  dangerColor,
  primaryColor,
  textPrimaryColor,
  textMutedColor,
  borderSubtleColor,
  boxShadow,
} from 'assets/jss/material-dashboard-react';

const dashboardStyle = {
  root: {
    animation: 'premiumFadeIn 0.35s ease-out both',
  },
  dashboardTitle: {
    padding: '0 4px 8px',
    marginBottom: 8,
  },
  pageTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 500,
    color: textPrimaryColor,
  },
  pageSubtitle: {
    margin: '6px 0 0',
    fontSize: 13,
    color: textMutedColor,
  },
  sectionTitle: {
    margin: '24px 4px 14px',
    fontSize: 15,
    fontWeight: 500,
    color: textPrimaryColor,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    minHeight: 0,
    padding: '20px 20px 14px',
    borderRadius: 14,
    border: `1px solid ${borderSubtleColor}`,
    ...boxShadow,
    marginTop: 8,
    marginBottom: 16,
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 6px 22px rgba(31, 42, 50, 0.1)',
    },
  },
  cardHeaderLabel: {
    margin: '0 0 6px',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.45,
    color: textPrimaryColor,
    paddingBottom: 10,
    borderBottom: `1px solid ${borderSubtleColor}`,
  },
  metricCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '22px 20px',
    minHeight: 140,
    borderRadius: 12,
    border: `1px solid ${borderSubtleColor}`,
    ...boxShadow,
    marginTop: 8,
    marginBottom: 16,
  },
  metricValue: {
    margin: 0,
    fontSize: 40,
    fontWeight: 500,
    lineHeight: 1.1,
    fontVariantNumeric: 'tabular-nums',
  },
  metricLabel: {
    margin: '10px 0 0',
    fontSize: 13,
    color: textMutedColor,
  },
  metricDivider: {
    width: '100%',
    height: 1,
    backgroundColor: borderSubtleColor,
    margin: '18px 0',
    border: 'none',
  },
  list: {
    padding: 0,
    margin: 0,
  },
  metricList: {
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
  },
  metricRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 2px',
    borderBottom: `1px solid ${borderSubtleColor}`,
    '&:last-child': {
      borderBottom: 'none',
      paddingBottom: 4,
    },
  },
  metricRowIcon: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 168, 188, 0.12)',
    color: primaryColor,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    // Explicit margins — gap alone looked too tight next to Arabic labels
    marginLeft: 16,
    marginRight: 0,
    '& svg': {
      fontSize: 17,
    },
  },
  metricRowLabel: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    lineHeight: 1.5,
    color: textMutedColor,
    wordSpacing: '0.25em',
    letterSpacing: '0.01em',
    // Space between label and count (count sits on the left in RTL)
    marginLeft: 16,
    marginRight: 0,
    paddingLeft: 4,
  },
  metricRowValue: {
    flexShrink: 0,
    minWidth: 36,
    textAlign: 'left',
    fontSize: 18,
    fontWeight: 500,
    lineHeight: 1,
    color: textPrimaryColor,
    fontVariantNumeric: 'tabular-nums',
    fontFeatureSettings: '"tnum"',
    letterSpacing: '0.03em',
  },
  listItem: {
    padding: '8px 0',
    borderBottom: `1px solid ${borderSubtleColor}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  listPrimary: {
    fontSize: 13,
    color: textMutedColor,
  },
  listSecondary: {
    fontSize: 16,
    fontWeight: 500,
    color: textPrimaryColor,
    fontVariantNumeric: 'tabular-nums',
  },
  avatar: {
    width: 32,
    height: 32,
    backgroundColor: 'rgba(0, 168, 188, 0.12)',
    color: primaryColor,
    '& svg': {
      fontSize: 18,
    },
  },
  successText: {
    color: successColor,
  },
  upArrowCardCategory: {
    width: '16px',
    height: '16px',
  },
  stats: {
    color: textMutedColor,
    display: 'inline-flex',
    fontSize: '12px',
    lineHeight: '22px',
    '& svg': {
      top: '4px',
      width: '16px',
      height: '16px',
      position: 'relative',
      marginRight: '3px',
    },
  },
  cardTitlePending: {
    color: textPrimaryColor,
  },
  cardTitleEscalation: {
    color: `${infoColor} !important`,
  },
  cardTitleProcessing: {
    color: `${primaryColor} !important`,
  },
  cardTitleDone: {
    color: `${dangerColor} !important`,
  },
  cardCategory: {
    color: textMutedColor,
    margin: '0',
    fontSize: '12px',
    marginTop: '0',
    paddingTop: '10px',
    marginBottom: '0',
  },
  cardCategoryWhite: {
    color: 'rgba(255,255,255,.62)',
    margin: '0',
    fontSize: '12px',
    marginTop: '0',
    marginBottom: '0',
  },
  cardTitle: {
    color: textPrimaryColor,
    marginTop: '10px',
    minHeight: 'auto',
    fontWeight: '500',
    marginBottom: '3px',
    textDecoration: 'none',
    fontSize: '50px',
    '& small': {
      color: textMutedColor,
      fontSize: '35%',
      fontWeight: '400',
      lineHeight: '1',
    },
  },
  cardTitleWhite: {
    color: '#FFFFFF',
    marginTop: '0px',
    minHeight: 'auto',
    fontWeight: '300',
    marginBottom: '3px',
    textDecoration: 'none',
    '& small': {
      color: '#777',
      fontSize: '65%',
      fontWeight: '400',
      lineHeight: '1',
    },
  },
};

export default dashboardStyle;
